import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import axios from "axios";
import {DataGrid, GridToolbar} from "@mui/x-data-grid";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Chart from "chart.js/auto";
import {baseUrl, userLocation} from "../Univ.js";
import {Link} from "react-router-dom";
function OpDashboard() {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [opData, setOpData] = useState([]);
    const [reviewData, setReviewData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isReviewLoading, setIsReviewLoading] = useState(false);
    // All consultants are shown by default.
    const [selectedConsultant, setSelectedConsultant] = useState("ALL");
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    // Convert a Date to YYYY-MM-DD using local time.
    // This avoids UTC date shifts caused by toISOString().
    const toDateString = useCallback((date) => {
        if (!date) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    }, []);
    const formatDate = useCallback((value) => {
        if (!value) return "";
        // Format YYYY-MM-DD directly to avoid timezone shifts.
        if (typeof value === "string") {
            const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (match) {
                return `${match[3]}/${match[2]}/${match[1]}`;
            }
        }
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }
        return date.toLocaleDateString("en-GB");
    }, []);
    // Fetch appointment history whenever the date range changes.
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams({
                start_date: toDateString(startDate), end_date: toDateString(endDate),
            });
            const response = await axios.get(`${baseUrl}op-appointment-view/?${params.toString()}`);
            setOpData(Array.isArray(response.data) ? response.data : response.data?.results || []);
        } catch (error) {
            console.error("Appointment API Error:", error);
            setOpData([]);
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate, toDateString]);
    // Fetch review patients for the selected date range.
    const fetchReviewData = useCallback(async () => {
        setIsReviewLoading(true);
        try {
            const params = new URLSearchParams({
                start_date: toDateString(startDate),
                end_date: toDateString(endDate),
                location: String(userLocation ?? ""),
            });
            const response = await axios.get(`${baseUrl}review-due-today/?${params.toString()}`);
            const data = response.data;
            setReviewData(Array.isArray(data?.reviews) ? data.reviews : Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Review API Error:", error);
            setReviewData([]);
        } finally {
            setIsReviewLoading(false);
        }
    }, [startDate, endDate, toDateString]);
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    useEffect(() => {
        fetchReviewData();
    }, [fetchReviewData]);
    // Build the consultant list dynamically from appointment data.

const consultants = useMemo(() => {
    const appointmentConsultants = opData.map(
        (item) => item.cons__consultant
    );

    const reviewConsultants = reviewData.map(
        (item) =>
            item.appt__cons__consultant ??
            item.cons__consultant ??
            item.consultant ??
            ""
    );

    const names = [
        ...appointmentConsultants,
        ...reviewConsultants,
    ]
        .filter((name) => name !== null && name !== undefined)
        .map((name) => String(name).trim())
        .filter(Boolean);

    return [...new Set(names)].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" })
    );
}, [opData, reviewData]);

    // If the selected consultant disappears after changing dates,
    // automatically return to All.

useEffect(() => {
    if (
        selectedConsultant !== "ALL" &&
        !consultants.some(
            (name) =>
                name.toLowerCase() ===
                selectedConsultant.toLowerCase()
        )
    ) {
        setSelectedConsultant("ALL");
    }
}, [consultants, selectedConsultant]);

    // Filter appointment data once, then reuse it for the table,
    // summary counts and chart.
    const filteredOpData = useMemo(() => {
        if (selectedConsultant === "ALL") {
            return opData;
        }
        return opData.filter((item) => String(item.cons__consultant ?? "").toLowerCase() === selectedConsultant.toLowerCase());
    }, [opData, selectedConsultant]);
    // Filter review patients by consultant.
    // Supports common consultant fields returned by Django.
    const filteredReviewData = useMemo(() => {
        if (selectedConsultant === "ALL") {
            return reviewData;
        }
        return reviewData.filter((item) => {
            const consultant = item.appt__cons__consultant ?? item.cons__consultant ?? item.consultant ?? "";
            return (String(consultant).trim().toLowerCase() === selectedConsultant.toLowerCase());
        });
    }, [reviewData, selectedConsultant]);
    // Review Patients grid rows.
    const reviewRows = useMemo(() => filteredReviewData.map((item, index) => ({
        id: item.id ?? `review-${index}`,
        last_appt_date: item.last_appt_date || item.add_date || "",
        cons: item.appt__cons__consultant ?? item.cons__consultant ?? item.consultant ?? "",
        consultant_id: item.appt__cons__id ?? null,

        pat_id: item.pat__pat_id || "",
        patient: item.pat__patient || "",
        mobile: item.pat__mobile || "",
        appt_date: item.appt__appt_date || "",
        review_date: item.review_date || "",
    })), [filteredReviewData]);
    const reviewColumns = useMemo(() => [{
        field: "last_appt_date",
        headerName: "Last Appt Date",
        width: 145,
        renderCell: (params) => formatDate(params.value),
    }, {
        field: "cons", headerName: "Consultant", width: 160,
    }, {
        field: "pat_id", headerName: "Patient ID", width: 120,
    }, {
        field: "patient", headerName: "Patient Name", width: 210,
    }, {
        field: "mobile", headerName: "Mobile", width: 140,
    }, {
        field: "appt_date", headerName: "Appt Date", width: 130, renderCell: (params) => formatDate(params.value),
    }, {
        field: "review_date", headerName: "Review Due", width: 130, renderCell: (params) => formatDate(params.value),
    },
    {
    field: "book_op",
    headerName: "Action",
    width: 130,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
        <Link
            to={`/fix-appointment/${params.row.consultant_id}`}
            className="btn btn-primary"
            target="_blank"
            rel="noopener noreferrer"
        >
            Book OP
        </Link>
    ),
},
    ], [formatDate]);
    // Appointment history grid rows.
    const appointmentRows = useMemo(() => filteredOpData.map((item, index) => ({
        id: item.id ?? `appointment-${index}`,
        appt_date: item.appt_date,
        cons__consultant: item.cons__consultant,
        appt_id: item.appt_id,
        tokid: item.tokid,
        pat__pat_id: item.pat__pat_id,
        pat__patient: item.pat__patient,
        pat__age: item.pat__age,
        pat__address1: item.pat__address1,
        appt_time: item.appt_time,
        status: item.status,
    })), [filteredOpData]);
    const appointmentColumns = useMemo(() => [{
        field: "appt_date", headerName: "Date", width: 110, renderCell: (params) => formatDate(params.value),
    }, {
        field: "cons__consultant", headerName: "Consultant", width: 160,
    }, {
        field: "tokid", headerName: "Token No", width: 110, renderCell: (params) => (<span className="badge bg-primary">
                        {params.value ?? ""}
                    </span>),
    }, {
        field: "appt_id", headerName: "Appt ID", width: 110,
    }, {
        field: "pat__pat_id", headerName: "Patient ID", width: 110,
    }, {
        field: "pat__patient", headerName: "Patient", width: 180,
    }, {
        field: "pat__age", headerName: "Age", width: 75,
    }, {
        field: "pat__address1", headerName: "Address", width: 250,
    }, {
        field: "appt_time", headerName: "Appt Slot", width: 120,
    }, {
        field: "status", headerName: "Status", width: 110, renderCell: (params) => {
            const status = Number(params.value);
            if (status === 1) {
                return <span className="badge bg-primary">Pending</span>;
            }
            if (status === 2) {
                return <span className="badge bg-warning text-dark">Waiting</span>;
            }
            if (status === 3) {
                return <span className="badge bg-success">Completed</span>;
            }
            if (status === 0) {
                return <span className="badge bg-danger">Cancelled</span>;
            }
            return <span className="badge bg-secondary">{params.value ?? "Unknown"}</span>;
        },
    },], [formatDate]);
    // Calculate counts from the consultant-filtered appointments.
    const appointmentCountsByConsultant = useMemo(() => {
        const counts = {};
        filteredOpData.forEach((item) => {
            const consultant = item.cons__consultant || "Unassigned";
            const type = String(item.type ?? "").toLowerCase();
            const status = Number(item.status);
            if (!counts[consultant]) {
                counts[consultant] = {
                    consultant, appointments: 0, pending: 0, new: 0, revisit: 0, completed: 0, cancel: 0,
                };
            }
            const row = counts[consultant];
            row.appointments++;
            if (status === 1 || status === 2) {
                row.pending++;
            }
            if (type === "new") {
                row.new++;
            }
            if (type === "revisit") {
                row.revisit++;
            }
            if (status === 3) {
                row.completed++;
            }
            if (status === 0) {
                row.cancel++;
            }
        });
        return Object.values(counts);
    }, [filteredOpData]);
    // Keep the Chart.js instance synchronized with filtered counts.
    useEffect(() => {
        if (!chartRef.current) return;
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy();
            chartInstanceRef.current = null;
        }
        const labels = ["Pending", "New", "Revisit", "Completed", "Cancel"];
        const datasets = appointmentCountsByConsultant.map((counts) => ({
            label: counts.consultant,
            data: [counts.pending, counts.new, counts.revisit, counts.completed, counts.cancel,],
            backgroundColor: ["rgba(255, 99, 132, 0.65)", "rgba(54, 162, 235, 0.65)", "rgba(255, 206, 86, 0.65)", "rgba(75, 192, 192, 0.65)", "rgba(153, 102, 255, 0.65)",],
        }));
        chartInstanceRef.current = new Chart(chartRef.current, {
            type: "bar", data: {
                labels, datasets,
            }, options: {
                responsive: true, maintainAspectRatio: true, plugins: {
                    legend: {
                        display: true, position: "bottom",
                    }, title: {
                        display: true,
                        text: selectedConsultant === "ALL" ? "Appointment Summary — All Consultants" : `Appointment Summary — ${selectedConsultant}`,
                    },
                }, scales: {
                    y: {
                        beginAtZero: true, ticks: {
                            precision: 0,
                        },
                    },
                },
            },
        });
        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
                chartInstanceRef.current = null;
            }
        };
    }, [appointmentCountsByConsultant, selectedConsultant]);
    const clearFilters = () => {
        setSelectedConsultant("ALL");
        setStartDate(null);
        setEndDate(null);
    };
    const gridSx = {
        boxShadow: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        backgroundColor: "background.paper",
        "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#0f766e", color: "#fff", fontWeight: 700,
        },
        "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
        },
        "& .MuiDataGrid-cell:hover": {
            color: "primary.main",
        },
    };
    return (<section className="container-fluid mt-3 mb-4">
            {/* Dashboard heading */}
            <div
                className="d-flex align-items-center gap-2 px-3 py-3 mb-3 rounded"
                style={{
                    background: "linear-gradient(100deg, #0f766e, #0891b2)", color: "#fff",
                }}
            >
                <i className="fa-solid fa-calendar-days fs-4"/>
                <h4 className="mb-0">OP Dashboard Report</h4>
            </div>

            {/* Shared filters */}
            <div className="card shadow-sm mb-4">
                <div className="card-header bg-light">
                    <strong>
                        <i className="fa-solid fa-filter me-2"/>
                        Dashboard Filters
                    </strong>
                </div>

                <div className="card-body">
                    <div className="row g-3 align-items-end">
                        <div className="col-12 col-md-4 col-lg-3">
                            <label
                                htmlFor="consultant-filter"
                                className="form-label fw-semibold"
                            >
                                Consultant
                            </label>

                            <select
                                id="consultant-filter"
                                className="form-select"
                                value={selectedConsultant}
                                onChange={(event) => setSelectedConsultant(event.target.value)}
                            >
                                <option value="ALL">All Consultants</option>

                                {consultants.map((consultant) => (<option
                                        key={consultant}
                                        value={consultant}
                                    >
                                        {consultant}
                                    </option>))}
                            </select>
                        </div>

                        <div className="col-12 col-md-4 col-lg-3">
                            <label className="form-label fw-semibold">
                                From Date
                            </label>
                            <DatePicker
                                selected={startDate}
                                onChange={(date) => {
                                    setStartDate(date);
                                    if (endDate && date && endDate < date) {
                                        setEndDate(null);
                                    }
                                }}
                                selectsStart
                                startDate={startDate}
                                endDate={endDate}
                                maxDate={endDate || undefined}
                                placeholderText="Select start date"
                                dateFormat="dd/MM/yyyy"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                isClearable
                                className="form-control"
                                wrapperClassName="w-100"
                                popperClassName="op-dashboard-calendar-popper"
                                popperPlacement="bottom-start"
                                portalId="root"
                            />
                        </div>

                        <div className="col-12 col-md-4 col-lg-3">
                            <label className="form-label fw-semibold">
                                To Date
                            </label>

                            <DatePicker
                                selected={endDate}
                                onChange={setEndDate}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                minDate={startDate || undefined}
                                placeholderText="Select end date"
                                dateFormat="dd/MM/yyyy"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                isClearable
                                className="form-control"
                                wrapperClassName="w-100"

                                popperClassName="op-dashboard-calendar-popper"
                                popperPlacement="bottom-start"
                                portalId="root"
                            />
                        </div>

                        <div className="col-12 col-lg-3">
                            <button
                                type="button"
                                className="btn btn-outline-secondary w-100"
                                onClick={clearFilters}
                            >
                                <i className="fa-solid fa-rotate-left me-2"/>
                                Clear Filters
                            </button>
                        </div>
                    </div>

                    <div className="d-flex flex-wrap gap-2 mt-3">
                        <span className="badge bg-primary p-2">
                            Consultant:{" "}
                            {selectedConsultant === "ALL" ? "All" : selectedConsultant}
                        </span>

                        <span className="badge bg-success p-2">
                            Appointments: {filteredOpData.length}
                        </span>

                        <span className="badge bg-info text-dark p-2">
                            Review Patients: {reviewRows.length}
                        </span>
                    </div>
                </div>
            </div>

            {/* Review Patients */}
            <div className="card shadow-sm mb-4">
                <div
                    className="card-header text-white d-flex align-items-center justify-content-between flex-wrap gap-2"
                    style={{backgroundColor: "#0f766e"}}>
                    <h5 className="mb-0">
                        <i className="fa-solid fa-user-clock me-2"/>
                        Review Patients
                    </h5>

                    <span className="badge bg-light text-dark">
                        {reviewRows.length} Patients
                    </span>
                </div>

                <div className="card-body p-2">
                    {isReviewLoading ? (<div className="text-center p-4">
                            <div
                                className="spinner-border text-primary"
                                role="status"
                            >
                                <span className="visually-hidden">
                                    Loading...
                                </span>
                            </div>
                        </div>) : (<DataGrid
                            rows={reviewRows}
                            columns={reviewColumns}
                            autoHeight
                            disableRowSelectionOnClick
                            slots={{toolbar: GridToolbar}}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 10, page: 0,
                                    },
                                },
                            }}
                            pageSizeOptions={[10, 25, 50, 100]}
                            sx={gridSx}
                        />)}
                </div>
            </div>

            {/* Consultant appointment summary */}
            <div className="card shadow-sm mb-4">

    <div className="card-header bg-light">
                    <strong>
                        <i className="fa-solid fa-filter me-2"/>
                        Patient Appointment Summary
                    </strong>
                </div>

                <div className="card-body">
                    {isLoading ? (<div className="text-center p-4">
                            <div className="spinner-border text-primary"/>
                        </div>) : appointmentCountsByConsultant.length === 0 ? (<div className="alert alert-info mb-0">
                            No appointment records found for the selected
                            filters.
                        </div>) : (<div className="table-responsive">
                            <table className="table-erp-modern">
                                <thead className="table-light">
                                <tr>
                                    <th>Consultant</th>
                                    <th>Appointments</th>
                                    <th>Pending</th>
                                    <th>New</th>
                                    <th>Revisit</th>
                                    <th>Completed</th>
                                    <th>Cancel</th>
                                </tr>
                                </thead>

                                <tbody>
                                {appointmentCountsByConsultant.map((counts) => (<tr key={counts.consultant}>
                                        <td className="fw-semibold">
                                            {counts.consultant}
                                        </td>
                                        <td>{counts.appointments}</td>
                                        <td>{counts.pending}</td>
                                        <td>{counts.new}</td>
                                        <td>{counts.revisit}</td>
                                        <td>
                                                    <span className="badge bg-success">
                                                        {counts.completed}
                                                    </span>
                                        </td>
                                        <td>
                                                    <span className="badge bg-danger">
                                                        {counts.cancel}
                                                    </span>
                                        </td>
                                    </tr>))}
                                </tbody>

                                <tfoot className="table-light fw-bold">
                                <tr>
                                    <td>Total</td>
                                    <td>
                                        {appointmentCountsByConsultant.reduce((sum, item) => sum + item.appointments, 0)}
                                    </td>
                                    <td>
                                        {appointmentCountsByConsultant.reduce((sum, item) => sum + item.pending, 0)}
                                    </td>
                                    <td>
                                        {appointmentCountsByConsultant.reduce((sum, item) => sum + item.new, 0)}
                                    </td>
                                    <td>
                                        {appointmentCountsByConsultant.reduce((sum, item) => sum + item.revisit, 0)}
                                    </td>
                                    <td>
                                        {appointmentCountsByConsultant.reduce((sum, item) => sum + item.completed, 0)}
                                    </td>
                                    <td>
                                        {appointmentCountsByConsultant.reduce((sum, item) => sum + item.cancel, 0)}
                                    </td>
                                </tr>
                                </tfoot>
                            </table>
                        </div>)}
                </div>
            </div>

            {/* Appointment chart */}
            <div className="card shadow-sm mb-4">
                <div className="card-header bg-light">
                    <h5 className="mb-0">
                        <i className="fa-solid fa-chart-bar me-2"/>
                        Appointment Analytics
                    </h5>
                </div>

                <div className="card-body">
                    {appointmentCountsByConsultant.length > 0 ? (<div style={{width: "100%", minHeight: 250}}>
                            <canvas ref={chartRef}/>
                        </div>) : (<div className="text-center text-muted p-3">
                            No chart data available.
                        </div>)}
                </div>
            </div>

            {/* Appointment history */}
            <div className="card shadow-sm mb-4">
                <div
                    className="card-header text-white d-flex justify-content-between align-items-center flex-wrap gap-2"
                    style={{backgroundColor: "#0f766e"}}>
                    <h5 className="mb-0">
                        <i className="fa-solid fa-list-check me-2"/>
                        View Patient Appointment History
                    </h5>

                    <span className="badge bg-light text-dark">
                        {appointmentRows.length} Appointments
                    </span>
                </div>

                <div className="card-body p-2">
                    {isLoading ? (<div className="text-center p-4">
                            <div
                                className="spinner-border text-primary"
                                role="status"
                            >
                                <span className="visually-hidden">
                                    Loading...
                                </span>
                            </div>
                        </div>) : (<DataGrid
                            rows={appointmentRows}
                            columns={appointmentColumns}
                            autoHeight
                            disableRowSelectionOnClick
                            slots={{toolbar: GridToolbar}}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 10, page: 0,
                                    },
                                },
                            }}
                            pageSizeOptions={[10, 25, 50, 100]}
                            sx={gridSx}
                        />)}
                </div>
            </div>
        </section>);
}
export default OpDashboard;
